// Pure, framework-free logic for D18 "auto-derive Segment palette from media".
//
// IMPORTANT — reuse, do NOT reimplement: the image/gif/gradient/pattern ->
// palette EXTRACTION + commit already exists as
// useSegmentPalettes().fetchSegmentPaletteFromFill (flag
// REACT_APP_SEGMENT_PALETTE_COLOR, segmentPaletteFromFill.js). D18 layers a
// debounce + an undo/history safety-net + a "Palette updated from image — Undo"
// toast ON TOP of that engine, gated by REACT_APP_SEG_PALETTE_AUTODERIVE. This
// file holds only the small PURE pieces of that layer so they stay unit-testable
// without React/Redux. Nothing here fetches or dispatches.

// The user-facing safety-net toast copy (D18: "colors changed without a click"
// gets an explicit Undo). App language only — "Palette", not a brand term.
export const AUTODERIVE_TOAST_MESSAGE = "Palette updated from image — Undo";

// A Segment fill/wallpaper "value" may be a bare URL string or an object with a
// `.src`. Return the raster image/GIF URL, or null when there is nothing to
// sample (gradient / solid color / pattern id / empty).
export function extractMediaUrl(value) {
  const raw =
    typeof value === "string" ? value : value && value.src ? value.src : null;
  if (!raw || typeof raw !== "string") return null;
  const url = raw.trim();
  if (!url) return null;
  if (
    /^(linear-gradient|radial-gradient|conic-gradient|rgb|rgba|hsl|hsla)\(/i.test(
      url
    )
  )
    return null;
  if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(url)) return null;
  return url;
}

// Only derive when the wallpaper is an actual image/GIF layer (isPattern falsy),
// not a repeating pattern (isPattern truthy) or a gradient/solid fill.
export function isDerivableWallpaper(value, isPattern) {
  if (isPattern) return false;
  return extractMediaUrl(value) !== null;
}

// Whether the media is a GIF. Pillow opens frame 0 by default server-side, so a
// GIF URL already derives from its FIRST FRAME through the existing extractor;
// this flag is surfaced only for telemetry/notes. Query strings/fragments are
// ignored.
export function isGif(url) {
  if (typeof url !== "string") return false;
  const path = url.split(/[?#]/)[0];
  return /\.gif$/i.test(path);
}

// A "manual palette lock" that should SUPPRESS auto-derive. D18 chose
// auto-WITHOUT a toggle, so no lock UI is introduced. This is the single
// check-point: today it reads the EXISTING manual-override marker
// `segmentColorManual` (written by useSegmentSetting when a user explicitly picks
// a Segment Color) plus any future explicit lock field. When any is set, the
// user's choice wins and media changes do NOT overwrite it.
export function isPaletteAutoDeriveLocked(segment) {
  const s = (segment && segment.settings) || {};
  return Boolean(
    s.segmentColorManual ||
      s.paletteAutoDeriveLocked ||
      s.paletteLocked ||
      s.lockPaletteFromMedia
  );
}

// Trailing debounce factory. `run(fn)` schedules fn after `wait` ms, cancelling
// any pending call — so rapid successive media selections derive ONCE, from the
// last selection (the existing engine's in-flight dedup is keyed per
// segment+seed; this collapses the bursts before they ever fire).
export function makeDebouncer(
  wait,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout
) {
  let handle = null;
  return {
    run(fn) {
      if (handle) clearTimeoutFn(handle);
      handle = setTimeoutFn(() => {
        handle = null;
        fn();
      }, wait);
    },
    cancel() {
      if (handle) clearTimeoutFn(handle);
      handle = null;
    },
    isPending() {
      return handle !== null;
    },
  };
}

export const AUTODERIVE_DEBOUNCE_MS = 600;
